// Stock email templates — data-only constants file (exempt from 250-line limit).
// Each template is a complete HTML document with inline styles for email clients.
// Placeholder colors for the Customize tab to find-and-replace:
//   #f97316 = primary (buttons, links, accents)
//   #111111 = header background + heading text
//   #f4f4f5 = outer body background

export interface StockTemplate {
  id: string
  name: string
  description: string
  subject: string
  html: string
  hasSlots: boolean
}

// ---------------------------------------------------------------------------
// Shared fragments
// ---------------------------------------------------------------------------

const F = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

function footer(): string {
  return `
          <tr>
            <td style="padding: 0 40px;">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr><td style="border-top: 1px solid #e4e4e7; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding: 32px 40px 40px;">
              <p style="font-family: ${F}; font-size: 13px; line-height: 20px; color: #71717a; margin: 0 0 8px;">
                You received this email from <strong style="color: #111111;">{{company_name}}</strong>.
              </p>
              <p style="font-family: ${F}; font-size: 13px; line-height: 20px; color: #71717a; margin: 0;">
                <a href="{{unsubscribe_url}}" style="color: #f97316; text-decoration: underline;">Unsubscribe</a>
              </p>
            </td>
          </tr>`
}

function wrap(title: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <title>${title}</title>
  <style>
    body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
    @media only screen and (max-width: 620px) {
      .email-container { width: 100% !important; max-width: 100% !important; }
      .fluid { max-width: 100% !important; height: auto !important; }
      .stack-column { display: block !important; width: 100% !important; max-width: 100% !important; }
      .stack-column-center { text-align: center !important; }
      .center-on-narrow { text-align: center !important; display: block !important; margin-left: auto !important; margin-right: auto !important; float: none !important; }
      table.center-on-narrow { display: inline-block !important; }
      .pm { padding-left: 20px !important; padding-right: 20px !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f5; font-family: ${F};">
  <center style="width: 100%; background-color: #f4f4f5;">
    <!--[if mso | IE]><table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
    <table class="email-container" align="center" role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; margin: 0 auto;">
      <!-- Spacer top -->
      <tr><td style="height: 24px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
${inner}
      <!-- Spacer bottom -->
      <tr><td style="height: 24px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
    </table>
    <!--[if mso | IE]></td></tr></table><![endif]-->
  </center>
</body>
</html>`
}

// ---------------------------------------------------------------------------
// 1. Newspaper
// ---------------------------------------------------------------------------

const newspaperHtml = wrap(
  "{{company_name}} \u2014 This Week's Top Stories",
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 28px 40px; border-radius: 12px 12px 0 0;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td>
                <h1 style="font-family: ${F}; font-size: 22px; font-weight: 700; color: #ffffff; margin: 0; letter-spacing: -0.3px;">{{company_name}}</h1>
              </td>
              <td align="right">
                <span style="font-family: ${F}; font-size: 12px; color: #a1a1aa; text-transform: uppercase; letter-spacing: 1.5px;">Weekly Newsletter</span>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <tr><td style="background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" role="presentation">

          <!-- slot:featured -->
          <tr>
            <td style="padding: 0;">
              <img src="https://placehold.co/600x300/111111/ffffff?text=Featured+Story" alt="Featured story" width="600" style="width: 100%; height: auto; display: block;" class="fluid">
            </td>
          </tr>
          <tr>
            <td style="padding: 32px 40px 28px;" class="pm">
              <p style="font-family: ${F}; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: #f97316; margin: 0 0 12px;">Featured</p>
              <h2 style="font-family: ${F}; font-size: 26px; font-weight: 700; color: #111111; margin: 0 0 12px; line-height: 1.25; letter-spacing: -0.5px;">The Headline of Your Featured Article Goes Here</h2>
              <p style="font-family: ${F}; font-size: 16px; line-height: 1.6; color: #52525b; margin: 0 0 20px;">A brief excerpt that draws the reader in and makes them want to click through to read the full story. Keep it punchy and informative.</p>
              <a href="#" style="display: inline-block; background-color: #f97316; color: #ffffff; font-family: ${F}; font-size: 14px; font-weight: 600; padding: 12px 28px; border-radius: 8px; text-decoration: none;">Read Full Story &rarr;</a>
            </td>
          </tr>
          <!-- /slot:featured -->

          <!-- Divider -->
          <tr>
            <td style="padding: 0 40px;" class="pm">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr><td style="border-top: 1px solid #e4e4e7; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>

          <!-- slot:articles -->
          <tr>
            <td style="padding: 28px 40px;" class="pm">
              <p style="font-family: ${F}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: #a1a1aa; margin: 0 0 20px;">More Stories</p>
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr>
                  <td class="stack-column" width="48%" valign="top" style="padding-right: 12px;">
                    <img src="https://placehold.co/260x160/e4e4e7/71717a?text=Article+1" alt="Article" width="260" style="width: 100%; height: auto; border-radius: 8px; display: block; margin-bottom: 14px;" class="fluid">
                    <p style="font-family: ${F}; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #f97316; margin: 0 0 8px;">Category</p>
                    <h3 style="font-family: ${F}; font-size: 17px; font-weight: 700; color: #111111; margin: 0 0 8px; line-height: 1.3;">Second Story Headline With a Catchy Title</h3>
                    <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 10px;">Brief excerpt of the article that gives readers a taste of what it covers.</p>
                    <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">Read more &rarr;</a>
                  </td>
                  <td width="4%"></td>
                  <td class="stack-column" width="48%" valign="top" style="padding-left: 12px;">
                    <img src="https://placehold.co/260x160/e4e4e7/71717a?text=Article+2" alt="Article" width="260" style="width: 100%; height: auto; border-radius: 8px; display: block; margin-bottom: 14px;" class="fluid">
                    <p style="font-family: ${F}; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #f97316; margin: 0 0 8px;">Category</p>
                    <h3 style="font-family: ${F}; font-size: 17px; font-weight: 700; color: #111111; margin: 0 0 8px; line-height: 1.3;">Third Story With Another Interesting Headline</h3>
                    <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 10px;">Brief excerpt of the article that gives readers a taste of what it covers.</p>
                    <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">Read more &rarr;</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- /slot:articles -->

          <!-- Divider -->
          <tr>
            <td style="padding: 0 40px;" class="pm">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr><td style="border-top: 1px solid #e4e4e7; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>

          <!-- slot:sponsor -->
          <tr>
            <td style="padding: 28px 40px;" class="pm">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #fafafa; border: 1px solid #e4e4e7; border-radius: 10px;">
                <tr>
                  <td style="padding: 24px 28px;">
                    <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                      <tr>
                        <td valign="middle">
                          <p style="font-family: ${F}; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: #a1a1aa; margin: 0 0 8px;">Sponsored by</p>
                          <p style="font-family: ${F}; font-size: 16px; font-weight: 700; color: #111111; margin: 0 0 6px;">Your Sponsor Name Here</p>
                          <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0;">A compelling one-liner about the sponsor's product or service that drives clicks.</p>
                        </td>
                        <td width="130" align="right" valign="middle">
                          <a href="#" style="display: inline-block; background-color: #111111; color: #ffffff; font-family: ${F}; font-size: 13px; font-weight: 600; padding: 10px 20px; border-radius: 8px; text-decoration: none;">Learn More</a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- /slot:sponsor -->

        </table>
      </td></tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 2. Announcement
// ---------------------------------------------------------------------------

const announcementHtml = wrap(
  'Big news from {{company_name}}',
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 24px 40px; border-radius: 12px 12px 0 0;">
          <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
        </td>
      </tr>

      <!-- Hero Image -->
      <tr>
        <td style="background-color: #ffffff;">
          <img src="https://placehold.co/600x320/111111/f97316?text=Big+Announcement" alt="Announcement" width="600" style="width: 100%; height: auto; display: block;" class="fluid">
        </td>
      </tr>

      <!-- Content -->
      <tr>
        <td style="background-color: #ffffff; padding: 40px 40px 16px;" class="pm">
          <h2 style="font-family: ${F}; font-size: 28px; font-weight: 700; color: #111111; margin: 0 0 16px; line-height: 1.2; letter-spacing: -0.5px;">We're launching something incredible</h2>
          <p style="font-family: ${F}; font-size: 16px; line-height: 1.65; color: #52525b; margin: 0 0 16px;">
            After months of hard work, we're thrilled to share our biggest update yet. This changes everything about how you work with our platform.
          </p>
          <p style="font-family: ${F}; font-size: 16px; line-height: 1.65; color: #52525b; margin: 0 0 16px;">
            Here's what you need to know: we've completely redesigned the experience from the ground up, focusing on speed, simplicity, and the features you've been asking for.
          </p>
          <ul style="font-family: ${F}; font-size: 16px; line-height: 1.65; color: #52525b; margin: 0 0 16px; padding-left: 20px;">
            <li style="margin-bottom: 8px;"><strong style="color: #111111;">Feature one</strong> &mdash; A brief description of what it does.</li>
            <li style="margin-bottom: 8px;"><strong style="color: #111111;">Feature two</strong> &mdash; Another key improvement worth highlighting.</li>
            <li style="margin-bottom: 0;"><strong style="color: #111111;">Feature three</strong> &mdash; The third thing they'll love about this.</li>
          </ul>
        </td>
      </tr>

      <!-- CTA -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 16px 40px 40px;" class="pm">
          <a href="#" style="display: inline-block; background-color: #f97316; color: #ffffff; font-family: ${F}; font-size: 16px; font-weight: 600; padding: 14px 36px; border-radius: 8px; text-decoration: none;">See What's New &rarr;</a>
        </td>
      </tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 3. Promotion
// ---------------------------------------------------------------------------

const promotionHtml = wrap(
  'Special offer from {{company_name}}',
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 24px 40px; border-radius: 12px 12px 0 0;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td>
                <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
              </td>
              <td align="right">
                <span style="font-family: ${F}; font-size: 12px; font-weight: 600; color: #f97316; text-transform: uppercase; letter-spacing: 1px;">Limited Time</span>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Discount Banner -->
      <tr>
        <td style="background-color: #f97316; padding: 36px 40px; text-align: center;">
          <p style="font-family: ${F}; font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; color: rgba(255,255,255,0.85); margin: 0 0 8px;">Exclusive Offer</p>
          <h2 style="font-family: ${F}; font-size: 48px; font-weight: 800; color: #ffffff; margin: 0 0 8px; letter-spacing: -1px;">40% OFF</h2>
          <p style="font-family: ${F}; font-size: 16px; color: rgba(255,255,255,0.9); margin: 0;">Use code <strong style="background-color: rgba(0,0,0,0.15); padding: 4px 14px; border-radius: 4px; letter-spacing: 1px;">SAVE40</strong> at checkout</p>
        </td>
      </tr>

      <!-- Product Card -->
      <tr>
        <td style="background-color: #ffffff; padding: 40px;" class="pm">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td align="center" style="padding-bottom: 28px;">
                <img src="https://placehold.co/400x400/f4f4f5/111111?text=Product+Image" alt="Product" width="400" style="width: 100%; max-width: 400px; height: auto; border-radius: 12px; display: block;" class="fluid">
              </td>
            </tr>
            <tr>
              <td align="center">
                <h3 style="font-family: ${F}; font-size: 22px; font-weight: 700; color: #111111; margin: 0 0 8px;">Premium Product Name</h3>
                <p style="font-family: ${F}; font-size: 16px; line-height: 1.5; color: #71717a; margin: 0 0 20px; max-width: 420px;">A brief but compelling description of what makes this product special and why they need it right now.</p>
                <table cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td style="padding-right: 12px;">
                      <span style="font-family: ${F}; font-size: 16px; color: #a1a1aa; text-decoration: line-through;">$99.00</span>
                    </td>
                    <td>
                      <span style="font-family: ${F}; font-size: 24px; font-weight: 700; color: #f97316;">$59.40</span>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- CTA -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 0 40px 40px;" class="pm">
          <a href="#" style="display: inline-block; background-color: #111111; color: #ffffff; font-family: ${F}; font-size: 16px; font-weight: 600; padding: 16px 48px; border-radius: 8px; text-decoration: none;">Shop Now &rarr;</a>
          <p style="font-family: ${F}; font-size: 13px; color: #a1a1aa; margin: 16px 0 0;">Offer expires in 48 hours. While supplies last.</p>
        </td>
      </tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 4. Digest
// ---------------------------------------------------------------------------

const digestHtml = wrap(
  '{{company_name}} \u2014 Monthly Digest',
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 28px 40px; border-radius: 12px 12px 0 0;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td>
                <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
              </td>
              <td align="right">
                <span style="font-family: ${F}; font-size: 12px; color: #a1a1aa;">Monthly Digest</span>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Intro -->
      <tr>
        <td style="background-color: #ffffff; padding: 40px 40px 24px;" class="pm">
          <h2 style="font-family: ${F}; font-size: 24px; font-weight: 700; color: #111111; margin: 0 0 12px; letter-spacing: -0.3px;">Here's what happened this month</h2>
          <p style="font-family: ${F}; font-size: 16px; line-height: 1.6; color: #52525b; margin: 0;">A quick summary of the updates, improvements, and news you might have missed.</p>
        </td>
      </tr>

      <!-- slot:updates -->
      <tr>
        <td style="background-color: #ffffff; padding: 8px 40px 32px;" class="pm">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">

            <!-- Update 1 -->
            <tr>
              <td style="padding: 20px 0; border-bottom: 1px solid #f4f4f5;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td width="44" valign="top">
                      <div style="width: 32px; height: 32px; background-color: #fff7ed; border-radius: 8px; text-align: center; line-height: 32px; font-size: 16px;">&#x1F680;</div>
                    </td>
                    <td valign="top">
                      <h3 style="font-family: ${F}; font-size: 16px; font-weight: 600; color: #111111; margin: 0 0 4px;">New Feature: Advanced Analytics</h3>
                      <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 8px;">Track open rates, click-through rates, and deliverability metrics in real-time with our redesigned analytics dashboard.</p>
                      <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">Learn more &rarr;</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Update 2 -->
            <tr>
              <td style="padding: 20px 0; border-bottom: 1px solid #f4f4f5;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td width="44" valign="top">
                      <div style="width: 32px; height: 32px; background-color: #f0fdf4; border-radius: 8px; text-align: center; line-height: 32px; font-size: 16px;">&#x2705;</div>
                    </td>
                    <td valign="top">
                      <h3 style="font-family: ${F}; font-size: 16px; font-weight: 600; color: #111111; margin: 0 0 4px;">Improved Deliverability</h3>
                      <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 8px;">Our new SMTP infrastructure delivers 99.5% of emails within 3 seconds. DKIM, SPF, and DMARC are handled automatically.</p>
                      <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">Read the blog post &rarr;</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Update 3 -->
            <tr>
              <td style="padding: 20px 0; border-bottom: 1px solid #f4f4f5;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td width="44" valign="top">
                      <div style="width: 32px; height: 32px; background-color: #faf5ff; border-radius: 8px; text-align: center; line-height: 32px; font-size: 16px;">&#x1F4E6;</div>
                    </td>
                    <td valign="top">
                      <h3 style="font-family: ${F}; font-size: 16px; font-weight: 600; color: #111111; margin: 0 0 4px;">SDK v2.0 Released</h3>
                      <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 8px;">Smaller bundle, tree-shakeable exports, and TypeScript types built-in. Upgrade in one command.</p>
                      <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">View changelog &rarr;</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Update 4 -->
            <tr>
              <td style="padding: 20px 0;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td width="44" valign="top">
                      <div style="width: 32px; height: 32px; background-color: #fefce8; border-radius: 8px; text-align: center; line-height: 32px; font-size: 16px;">&#x1F4AC;</div>
                    </td>
                    <td valign="top">
                      <h3 style="font-family: ${F}; font-size: 16px; font-weight: 600; color: #111111; margin: 0 0 4px;">Community Spotlight</h3>
                      <p style="font-family: ${F}; font-size: 14px; line-height: 1.5; color: #71717a; margin: 0 0 8px;">See how teams are using our platform to send millions of transactional emails every month.</p>
                      <a href="#" style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #f97316; text-decoration: none;">Read case studies &rarr;</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

          </table>
        </td>
      </tr>
      <!-- /slot:updates -->

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 5. Feedback
// ---------------------------------------------------------------------------

const feedbackHtml = wrap(
  'How are we doing, {{name}}?',
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 24px 40px; border-radius: 12px 12px 0 0;">
          <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
        </td>
      </tr>

      <!-- Content -->
      <tr>
        <td style="background-color: #ffffff; padding: 48px 40px 24px;" class="pm">
          <h2 style="font-family: ${F}; font-size: 26px; font-weight: 700; color: #111111; margin: 0 0 12px; line-height: 1.2; letter-spacing: -0.3px; text-align: center;">How are we doing?</h2>
          <p style="font-family: ${F}; font-size: 16px; line-height: 1.6; color: #52525b; margin: 0; text-align: center; max-width: 440px; margin-left: auto; margin-right: auto;">
            Hi {{name}}, we'd love to hear your thoughts. Your feedback helps us build a better product for everyone.
          </p>
        </td>
      </tr>

      <!-- Rating Scale -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 28px 40px;" class="pm">
          <p style="font-family: ${F}; font-size: 13px; font-weight: 600; color: #a1a1aa; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 20px;">Rate your experience</p>
          <table cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td align="center" style="padding: 0 5px;">
                <a href="#" style="display: inline-block; width: 52px; height: 52px; line-height: 52px; text-align: center; font-size: 26px; background-color: #fef2f2; border-radius: 12px; text-decoration: none;">&#x1F620;</a>
                <p style="font-family: ${F}; font-size: 11px; color: #a1a1aa; margin: 6px 0 0;">1</p>
              </td>
              <td align="center" style="padding: 0 5px;">
                <a href="#" style="display: inline-block; width: 52px; height: 52px; line-height: 52px; text-align: center; font-size: 26px; background-color: #fff7ed; border-radius: 12px; text-decoration: none;">&#x1F641;</a>
                <p style="font-family: ${F}; font-size: 11px; color: #a1a1aa; margin: 6px 0 0;">2</p>
              </td>
              <td align="center" style="padding: 0 5px;">
                <a href="#" style="display: inline-block; width: 52px; height: 52px; line-height: 52px; text-align: center; font-size: 26px; background-color: #fefce8; border-radius: 12px; text-decoration: none;">&#x1F610;</a>
                <p style="font-family: ${F}; font-size: 11px; color: #a1a1aa; margin: 6px 0 0;">3</p>
              </td>
              <td align="center" style="padding: 0 5px;">
                <a href="#" style="display: inline-block; width: 52px; height: 52px; line-height: 52px; text-align: center; font-size: 26px; background-color: #f0fdf4; border-radius: 12px; text-decoration: none;">&#x1F642;</a>
                <p style="font-family: ${F}; font-size: 11px; color: #a1a1aa; margin: 6px 0 0;">4</p>
              </td>
              <td align="center" style="padding: 0 5px;">
                <a href="#" style="display: inline-block; width: 52px; height: 52px; line-height: 52px; text-align: center; font-size: 26px; background-color: #ecfdf5; border-radius: 12px; text-decoration: none;">&#x1F929;</a>
                <p style="font-family: ${F}; font-size: 11px; color: #a1a1aa; margin: 6px 0 0;">5</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Divider -->
      <tr>
        <td style="background-color: #ffffff; padding: 0 40px;" class="pm">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr><td style="border-top: 1px solid #f4f4f5; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td></tr>
          </table>
        </td>
      </tr>

      <!-- CTA -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 28px 40px 48px;" class="pm">
          <p style="font-family: ${F}; font-size: 15px; line-height: 1.5; color: #71717a; margin: 0 0 20px;">Want to share more detailed feedback?</p>
          <a href="#" style="display: inline-block; background-color: #f97316; color: #ffffff; font-family: ${F}; font-size: 15px; font-weight: 600; padding: 14px 32px; border-radius: 8px; text-decoration: none;">Take Our Quick Survey &rarr;</a>
          <p style="font-family: ${F}; font-size: 13px; color: #a1a1aa; margin: 14px 0 0;">It only takes 2 minutes. We read every response.</p>
        </td>
      </tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 6. Event
// ---------------------------------------------------------------------------

const eventHtml = wrap(
  "You're invited: {{event_name}}",
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 24px 40px; border-radius: 12px 12px 0 0;">
          <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
        </td>
      </tr>

      <!-- Hero Banner -->
      <tr>
        <td style="background-color: #ffffff; padding: 40px 40px 0;" class="pm">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #111111; border-radius: 12px;">
            <tr>
              <td style="padding: 44px 32px; text-align: center;">
                <p style="font-family: ${F}; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; color: #f97316; margin: 0 0 16px;">You're Invited</p>
                <h2 style="font-family: ${F}; font-size: 28px; font-weight: 700; color: #ffffff; margin: 0 0 8px; line-height: 1.2; letter-spacing: -0.5px;">{{event_name}}</h2>
                <p style="font-family: ${F}; font-size: 15px; color: #a1a1aa; margin: 0;">Hosted by {{company_name}}</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Date/Time + Location Cards -->
      <tr>
        <td style="background-color: #ffffff; padding: 28px 40px;" class="pm">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <!-- Date Card -->
              <td class="stack-column" width="48%" valign="top">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border: 1px solid #e4e4e7; border-radius: 12px; overflow: hidden;">
                  <tr>
                    <td style="background-color: #f97316; padding: 8px; text-align: center;">
                      <p style="font-family: ${F}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #ffffff; margin: 0;">April</p>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 16px; text-align: center;">
                      <p style="font-family: ${F}; font-size: 36px; font-weight: 800; color: #111111; margin: 0 0 4px; line-height: 1;">15</p>
                      <p style="font-family: ${F}; font-size: 13px; color: #71717a; margin: 0;">Tuesday</p>
                      <p style="font-family: ${F}; font-size: 14px; font-weight: 600; color: #111111; margin: 10px 0 0;">2:00 PM &ndash; 4:00 PM</p>
                      <p style="font-family: ${F}; font-size: 12px; color: #a1a1aa; margin: 4px 0 0;">Eastern Time (ET)</p>
                    </td>
                  </tr>
                </table>
              </td>
              <td width="4%"></td>
              <!-- Location Card -->
              <td class="stack-column" width="48%" valign="top">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border: 1px solid #e4e4e7; border-radius: 12px;">
                  <tr>
                    <td style="padding: 28px 20px; text-align: center;">
                      <div style="display: inline-block; background-color: #ecfdf5; width: 44px; height: 44px; line-height: 44px; border-radius: 12px; font-size: 22px; text-align: center; margin-bottom: 14px;">&#x1F30D;</div>
                      <p style="font-family: ${F}; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #a1a1aa; margin: 0 0 10px;">Location</p>
                      <span style="display: inline-block; font-family: ${F}; font-size: 12px; font-weight: 600; color: #059669; background-color: #ecfdf5; padding: 4px 14px; border-radius: 20px; margin-bottom: 12px;">Online Event</span>
                      <p style="font-family: ${F}; font-size: 14px; color: #71717a; margin: 0;">Join from anywhere.<br>Link sent after RSVP.</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Description -->
      <tr>
        <td style="background-color: #ffffff; padding: 0 40px 16px;" class="pm">
          <h3 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #111111; margin: 0 0 12px;">About this event</h3>
          <p style="font-family: ${F}; font-size: 15px; line-height: 1.65; color: #52525b; margin: 0 0 12px;">
            Join us for an interactive session where we'll dive deep into the latest developments and share our roadmap for the next quarter. Whether you're a power user or just getting started, there's something for everyone.
          </p>
          <p style="font-family: ${F}; font-size: 15px; line-height: 1.65; color: #52525b; margin: 0;">
            <strong style="color: #111111;">What you'll learn:</strong>
          </p>
          <ul style="font-family: ${F}; font-size: 15px; line-height: 1.65; color: #52525b; margin: 8px 0 0; padding-left: 20px;">
            <li style="margin-bottom: 4px;">Live demo of upcoming features</li>
            <li style="margin-bottom: 4px;">Q&amp;A with the founding team</li>
            <li>Exclusive early access for attendees</li>
          </ul>
        </td>
      </tr>

      <!-- CTA -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 24px 40px 20px;" class="pm">
          <a href="#" style="display: inline-block; background-color: #f97316; color: #ffffff; font-family: ${F}; font-size: 16px; font-weight: 600; padding: 16px 48px; border-radius: 8px; text-decoration: none;">RSVP Now &rarr;</a>
          <p style="font-family: ${F}; font-size: 13px; color: #a1a1aa; margin: 14px 0 0;">Free to attend &middot; 127 spots remaining</p>
        </td>
      </tr>

      <!-- Add to Calendar -->
      <tr>
        <td align="center" style="background-color: #ffffff; padding: 0 40px 40px;" class="pm">
          <table cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="padding: 0 8px;">
                <a href="#" style="font-family: ${F}; font-size: 13px; color: #71717a; text-decoration: underline;">Google Calendar</a>
              </td>
              <td style="font-family: ${F}; color: #d4d4d8;">&middot;</td>
              <td style="padding: 0 8px;">
                <a href="#" style="font-family: ${F}; font-size: 13px; color: #71717a; text-decoration: underline;">Apple Calendar</a>
              </td>
              <td style="font-family: ${F}; color: #d4d4d8;">&middot;</td>
              <td style="padding: 0 8px;">
                <a href="#" style="font-family: ${F}; font-size: 13px; color: #71717a; text-decoration: underline;">Outlook</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// 7. Blank
// ---------------------------------------------------------------------------

const blankHtml = wrap(
  '{{subject}}',
  `
      <!-- Header -->
      <tr>
        <td style="background-color: #111111; padding: 24px 40px; border-radius: 12px 12px 0 0;">
          <h1 style="font-family: ${F}; font-size: 18px; font-weight: 700; color: #ffffff; margin: 0;">{{company_name}}</h1>
        </td>
      </tr>

      <!-- Content Area -->
      <tr>
        <td style="background-color: #ffffff; padding: 40px;" class="pm">
          <p style="font-family: ${F}; font-size: 16px; line-height: 1.6; color: #52525b; margin: 0;">
            Start writing your email content here. Replace this text with your message.
          </p>
        </td>
      </tr>

      <!-- Footer -->
${footer()}

      <tr>
        <td style="background-color: #ffffff; border-radius: 0 0 12px 12px; height: 1px; font-size: 1px; line-height: 1px;">&nbsp;</td>
      </tr>`,
)

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const STOCK_TEMPLATES: StockTemplate[] = [
  {
    id: 'newspaper',
    name: 'Newspaper',
    description:
      'Content-heavy newsletter with a featured hero, two-column articles, and a sponsor slot.',
    subject: "{{company_name}} \u2014 This Week's Top Stories",
    html: newspaperHtml,
    hasSlots: true,
  },
  {
    id: 'announcement',
    name: 'Announcement',
    description: 'Single story or product update with a hero image and one clear call-to-action.',
    subject: 'Big news from {{company_name}}',
    html: announcementHtml,
    hasSlots: false,
  },
  {
    id: 'promotion',
    name: 'Promotion',
    description: 'Product or sale offer with a bold discount banner, product card, and shop CTA.',
    subject: 'Special offer from {{company_name}}',
    html: promotionHtml,
    hasSlots: false,
  },
  {
    id: 'digest',
    name: 'Digest',
    description: 'Weekly or monthly roundup of updates, each with an icon, title, and link.',
    subject: '{{company_name}} \u2014 Monthly Digest',
    html: digestHtml,
    hasSlots: true,
  },
  {
    id: 'feedback',
    name: 'Feedback',
    description: 'NPS-style review request with an emoji rating scale and survey CTA.',
    subject: 'How are we doing, {{name}}?',
    html: feedbackHtml,
    hasSlots: false,
  },
  {
    id: 'event',
    name: 'Event',
    description: 'Event invitation with a date card, location badge, description, and RSVP button.',
    subject: "You're invited: {{event_name}}",
    html: eventHtml,
    hasSlots: false,
  },
  {
    id: 'blank',
    name: 'Blank',
    description:
      'Clean responsive boilerplate with header, empty content area, and unsubscribe footer.',
    subject: '{{subject}}',
    html: blankHtml,
    hasSlots: false,
  },
]
